// adoptionAddendumClient — AUTHENTICATED staff client for the Lease Adoption
// Addendum send surface (/send/adoption-addendum).
//
// WEL-131: this is the ADD-ONLY join document. The Leaseholder Change agreement
// refuses to send without at least one OUTGOING resident, so a household where
// people only JOIN had no path at all; this addendum adds residents to the
// existing lease and leaves everyone already on it in place.
//
// Two endpoints, Bearer-authed via the shared `api` helper (wire shapes mirror
// apps/api/src/routes/adoptionAddendum.ts — keep in sync):
//
//   • GET  /api/adoption-addendum/candidates → AdoptionAddendumCandidates
//     APPLICATION-FIRST step 1: every APPROVED application group whose unit has
//     an active tenancy. Each candidate is the COMPLETE working set — current
//     leaseholders, both sitting-lease dates, premises address, applicants — so
//     picking one needs no second fetch. `sendEnabled` reports the send gate.
//
//   • POST /api/adoption-addendum/:groupId/send → SendAdoptionAddendumResult
//     Renders the addendum, creates the silent DocuSeal submission, and emails
//     every resident party their /sign/:uuid link. Miles ('Landlord') is invited
//     by the webhook once they have all signed. Errors:
//       409 missing signer email(s) → { error, missing: string[] }
//       503 send gate off          → AdoptionAddendumNotEnabledError

import { api, ApiError } from './api';
import type {
  LeaseholderChangeApplicant,
  LeaseholderChangeResident,
} from './leaseholderChangeClient';

// ---------------------------------------------------------------------------
// Wire types (mirror apps/api/src/lib/tenancyJoinCandidates.ts)
// ---------------------------------------------------------------------------

/**
 * One application-first candidate. Structurally the leaseholder change's
 * candidate plus `leaseEndDate` — the addendum's prose prints BOTH lease bounds
 * ("the Lease Agreement dated …", "will expire on …"), so both are prefilled.
 * The person shapes are shared with the leaseholder change deliberately: it is
 * the same tenancy and the same approved application, just a different document.
 */
export interface AdoptionAddendumCandidate {
  /** The application group id — the send is keyed to it (household-scoped). */
  groupId: string;
  propertyId: string;
  unitId: string;
  propertyAddress: string;
  submittedAt: string | null;
  /** yyyy-mm-dd, or '' when AppFolio carries none on file. */
  leaseStartDate: string;
  /** yyyy-mm-dd, or '' when AppFolio carries none on file. */
  leaseEndDate: string;
  /** The signing parties already on the lease — they sign the addendum too. */
  currentResidents: LeaseholderChangeResident[];
  /** The application's members — the residents being added. */
  applicants: LeaseholderChangeApplicant[];
}

export interface AdoptionAddendumCandidates {
  candidates: AdoptionAddendumCandidate[];
  /** Whether the send gate (DOCUSEAL_SEND_ENABLED) is on. */
  sendEnabled: boolean;
}

/** One party on the send body. `email` is required for anyone who signs. */
export interface AdoptionAddendumPerson {
  firstName: string;
  lastName: string;
  email: string;
}

export interface SendAdoptionAddendumBody {
  /** Everyone already on the lease — they consent to the addition and sign. */
  currentResidents: AdoptionAddendumPerson[];
  /** The residents being ADDED. At least one. */
  adoptees: AdoptionAddendumPerson[];
  premises: string;
  /** yyyy-mm-dd — the API renders it as the long legal form. */
  startDate: string;
  /** yyyy-mm-dd — the API renders it as the long legal form. */
  endDate: string;
  /** Free text; the API wraps it in the document's verbatim legal lead-in. */
  otherTerms?: string;
  /** Explicit intent to send ANOTHER addendum for a group that already has one. */
  confirmNewSend?: boolean;
}

/** One signer on the created submission, echoed back by `send`. */
export interface AdoptionAddendumSubmitter {
  /** Resident1..N (parallel) or Landlord (countersigns after them). */
  role: string;
  name?: string;
  email?: string;
  /** 0 = resident parties; 1 = Landlord. */
  order?: number;
  status: string;
  sentAt?: string;
}

export interface SendAdoptionAddendumResult {
  documentType: string;
  docusealSubmissionId: number;
  versionId: string;
  /** The frozen resident-party count the X/Y status is derived against. */
  adultResidentCount: number;
  status: string;
  submitters: AdoptionAddendumSubmitter[];
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Thrown on a 503 — the send gate is off. The page renders the dormant state. */
export class AdoptionAddendumNotEnabledError extends Error {
  constructor(message = 'Adoption addendum sends are not enabled yet.') {
    super(message);
    this.name = 'AdoptionAddendumNotEnabledError';
  }
}

/**
 * Read the `{ error, missing | detail }` body off a rejected send. The route
 * answers 409 with `missing: string[]` (names with no email) and 400 with
 * `issues`; both collapse to a message plus a list the page can render.
 */
export function adoptionAddendumErrorDetail(err: unknown): {
  message: string;
  detail: string[];
} | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as
    | { error?: string; missing?: unknown; detail?: unknown }
    | null;
  const list = Array.isArray(body?.missing)
    ? (body.missing as string[])
    : Array.isArray(body?.detail)
      ? (body.detail as string[])
      : [];
  return {
    message: body?.error ?? `Could not send the addendum (HTTP ${err.status})`,
    detail: list,
  };
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** Load the application-first candidates (approved groups joining a tenancy). */
export function getAdoptionAddendumCandidates(): Promise<AdoptionAddendumCandidates> {
  return api
    .get<Partial<AdoptionAddendumCandidates>>('/api/adoption-addendum/candidates')
    .then((raw) => ({
      candidates: raw.candidates ?? [],
      sendEnabled: raw.sendEnabled ?? false,
    }));
}

/**
 * Render + send the addendum to every resident party. Throws
 * AdoptionAddendumNotEnabledError on a 503; 400/409 propagate as ApiError
 * (read via adoptionAddendumErrorDetail).
 */
export async function sendAdoptionAddendum(
  groupId: string,
  body: SendAdoptionAddendumBody,
): Promise<SendAdoptionAddendumResult> {
  try {
    const raw = await api.post<Partial<SendAdoptionAddendumResult>>(
      `/api/adoption-addendum/${encodeURIComponent(groupId)}/send`,
      body,
    );
    return {
      documentType: raw.documentType ?? 'adoption_addendum',
      docusealSubmissionId: raw.docusealSubmissionId ?? 0,
      versionId: raw.versionId ?? '',
      adultResidentCount: raw.adultResidentCount ?? 0,
      status: raw.status ?? '',
      submitters: raw.submitters ?? [],
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      throw new AdoptionAddendumNotEnabledError(
        (err.body as { error?: string } | null)?.error ??
          'Adoption addendum sends are not enabled yet.',
      );
    }
    throw err;
  }
}
